import React, { useRef } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FaImage, FaTrash, FaCloudUploadAlt } from 'react-icons/fa';
import './MediaUploadSection.scss';

interface MediaUploadSectionProps {
  imageFiles: File[];
  handleImageFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (index: number) => void;
}

const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
  imageFiles,
  handleImageFileChange,
  handleRemoveImage
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      const event = {
        target: { files: imageFiles }
      } as any;
      handleImageFileChange(event);
    }
  };

  return (
    <div className={`media-upload-section ${imageFiles.length === 0 ? 'no-images' : ''}`}>
      <div className="section-header">
        <FaImage className="section-icon" />
        <div className="section-title-group">
          <h6 className="section-title">Imágenes del producto</h6>
          <span className="optional-badge">Opcional</span>
        </div>
        <span className="image-count">
          {imageFiles.length} imagen{imageFiles.length !== 1 ? 'es' : ''}
        </span>
      </div>

      {/* Upload Drop Zone */}
      <div 
        className="upload-drop-zone"
        onClick={handleDropZoneClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <FaCloudUploadAlt className="upload-icon" />
        <div className="upload-content">
          <h6 className="upload-title">Arrastra imágenes aquí</h6>
          <p className="upload-subtitle">o haz clic para seleccionar archivos</p>
          <div className="upload-formats">
            Formatos: JPG, PNG, GIF • Máximo 5MB por imagen
          </div>
        </div>
        
        <Form.Control
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Image Preview Grid */}
      {imageFiles.length > 0 && (
        <div className="image-preview-grid">
          {imageFiles.map((image, index) => (
            <div key={index} className="image-preview-item">
              <div className="image-wrapper">
                <img 
                  src={URL.createObjectURL(image)} 
                  alt={`Preview ${index + 1}`}
                  className="preview-image"
                />
                <div className="image-overlay">
                  <Button
                    variant="none"
                    className="remove-image-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>
              <div className="image-info">
                <span className="image-name">{image.name}</span>
                <span className="image-size">
                  {(image.size / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {imageFiles.length === 0 && (
        <div className="empty-state">
          <FaImage className="empty-icon" />
          <p className="empty-text">No hay imágenes seleccionadas</p>
        </div>
      )}
    </div>
  );
};

export default MediaUploadSection;
